import { useAtom } from "jotai";
import { atomFamily, atomWithStorage } from "jotai/utils";
import { useCallback, useEffect, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { useCloseTabsForEntity } from "@/features/model-tool";
import { useCurrentAccount } from "@/providers/account";
import dayjs from "@/utils/date";

import { createModelToolStorageKey } from "../utils/createLocalStorageKey";

export type DraftModel = {
  id: string;
  name: string;
  weldSql: string;
  number: number;
  createdAt: string;
  updatedAt: string;
  hidden?: boolean;
  isBackup?: boolean;
};
const draftsAtom = atomFamily((accountId: string) =>
  atomWithStorage<DraftModel[]>(
    createModelToolStorageKey("draft_models", accountId),
    [],
  ),
);

export const useDraftsAtom = () => {
  const { id } = useCurrentAccount();
  return useAtom(draftsAtom(id));
};

function isDraftExpired(draft: DraftModel) {
  const yesterday = dayjs().subtract(1, "day");
  return dayjs(draft.updatedAt).isBefore(yesterday);
}

function isDraftArchived(draft: DraftModel) {
  return draft.hidden || isDraftExpired(draft);
}

export function useAchivedDrafts() {
  const [drafts] = useDraftsAtom();
  return useMemo(() => {
    return drafts.filter(isDraftArchived);
  }, [drafts]);
}

export const useHasArchivedDrafts = () => useAchivedDrafts().length > 0;

export function useActiveDrafts() {
  const [drafts] = useDraftsAtom();
  return useMemo(() => {
    return drafts
      .filter((m) => !isDraftArchived(m))
      .sort((a, b) => {
        return a.number - b.number;
      });
  }, [drafts]);
}

export const useDrafts = () => {
  const [, setDrafts] = useDraftsAtom();

  const drafts = useActiveDrafts();
  const archived = useAchivedDrafts();

  //auto remove expired drafts that are empty
  useEffect(() => {
    const emptyArchivedDraftIds = archived
      .filter((m) => {
        return m.weldSql.length === 0;
      })
      .map((m) => m.id);

    if (emptyArchivedDraftIds.length > 0) {
      setDrafts((p) => p.filter((m) => !emptyArchivedDraftIds.includes(m.id)));
    }
  }, [archived, setDrafts]);

  return { drafts, archived };
};

export const useCreateDraftModel = () => {
  const [draftModels, setDraftModels] = useDraftsAtom();

  //drafts that are not archived:
  const { drafts } = useDrafts();
  const lowestAvailableNr = useMemo(() => {
    //find lowest available free number for new draft
    const numbers = drafts.map((m) => m.number);
    let number = 0;
    while (numbers.includes(number)) {
      number++;
    }
    return number;
  }, [drafts]);

  return useCallback(
    (weldSql: string = "") => {
      const timeStamp = dayjs().toISOString();

      const newDraft: DraftModel = {
        id: uuid(),
        name: "Unsaved draft",
        weldSql,
        number: lowestAvailableNr,
        createdAt: timeStamp,
        updatedAt: timeStamp,
      };

      setDraftModels([...draftModels, newDraft]);
      return newDraft;
    },
    [draftModels, lowestAvailableNr, setDraftModels],
  );
};

function generateBackupFilename(baseName: string): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");

  // Format ISO-8601 inspired but with local time and without milliseconds
  const timestamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  return `${baseName}_backup_${timestamp}`;
}

export const useModelBackupDraft = () => {
  const [draftModels, setDraftModels] = useDraftsAtom();
  return useCallback(
    (weldSql: string, name: string) => {
      const timeStamp = dayjs().toISOString();
      const newDraft: DraftModel = {
        id: uuid(),
        name: generateBackupFilename(name),
        weldSql,
        number: -1,
        createdAt: timeStamp,
        updatedAt: timeStamp,
        isBackup: true,
      };

      setDraftModels([...draftModels, newDraft]);
      return newDraft;
    },
    [draftModels, setDraftModels],
  );
};

export const useDraft = (
  draftId: string,
  options?: {
    skip?: boolean;
  },
) => {
  const [draftModels] = useDraftsAtom();

  const skip = options?.skip;
  const draft = useMemo(() => {
    if (skip) return undefined;
    return draftModels.find((m) => m.id === draftId);
  }, [draftModels, draftId, skip]);

  return draft;
};

export const useUpdateDraftModel = (draftId: string) => {
  const [draftModels, setDraftModels] = useDraftsAtom();
  return useCallback(
    (weldSql: string) => {
      const newDraftModels = draftModels.map((m) =>
        m.id === draftId
          ? {
              ...m,
              weldSql,
              updatedAt: dayjs().toISOString(),
              hidden: undefined,
              number: isDraftArchived(m)
                ? getLowestAvailableDraftNumber(draftModels)
                : m.number,
            }
          : m,
      );
      setDraftModels(newDraftModels);
    },
    [draftModels, draftId, setDraftModels],
  );
};

export const useHideModelDraft = () => {
  const [, setDraftModels] = useDraftsAtom();

  const closeTabsForEntity = useCloseTabsForEntity();

  return useCallback(
    (draftId: string) => {
      closeTabsForEntity({ type: "draft", id: draftId });

      setDraftModels((p) =>
        p.map((m) =>
          m.id === draftId
            ? {
                ...m,
                hidden: true,
              }
            : m,
        ),
      );
    },
    [setDraftModels, closeTabsForEntity],
  );
};

function getLowestAvailableDraftNumber(drafts: DraftModel[]) {
  //find lowest available free number for new draft
  const numbers = drafts.map((m) => m.number);
  let number = 0;
  while (numbers.includes(number)) {
    number++;
  }
  return number;
}

export const useRestoreDraft = () => {
  const [, setDraftModels] = useDraftsAtom();

  const { drafts } = useDrafts();

  return useCallback(
    (draftId: string) => {
      const lowestAvailableNr = getLowestAvailableDraftNumber(drafts);
      setDraftModels((p) =>
        p.map((m) =>
          m.id === draftId
            ? {
                ...m,
                hidden: false,
                updatedAt: dayjs().toISOString(),
                number: lowestAvailableNr,
              }
            : m,
        ),
      );
      return { newOrder: lowestAvailableNr };
    },
    [drafts, setDraftModels],
  );
};

export const useDeleteModelDraft = () => {
  const [, setDraftModels] = useDraftsAtom();

  return useCallback(
    (draftId: string, _hidden: boolean = true) => {
      setDraftModels((p) => p.filter((m) => m.id !== draftId));
    },
    [setDraftModels],
  );
};
